/**
 * Plausibility check for OCR'd time values.
 *
 * Rationale: handwritten Stundenliste times follow rounding conventions —
 * almost always {00, 15, 30, 45} (quarter-hours), sometimes 5-minute steps.
 * Atypical minute values (08:03, 12:07) are most often OCR misreads where
 * a 0 was confused with a 3, an 8 with a 3, etc. Flag for human review.
 *
 * Three buckets:
 *   - "ok"     — quarter-hour: {00, 15, 30, 45} → no marker
 *   - "yellow" — 5-min raster: {05, 10, 20, 25, 35, 40, 50, 55} → warn
 *   - "red"    — anything else (01-04, 06-09, 11-14, ...) → atypical
 *
 * Independent from confidence: a low-conf 08:00 is still a typical value,
 * a high-conf 08:03 is still atypical (likely VLM read-error).
 */

export type Plausibility = "ok" | "yellow" | "red";

const QUARTER = new Set([0, 15, 30, 45]);
const FIVE_MIN = new Set([5, 10, 20, 25, 35, 40, 50, 55]);

/**
 * Classify a time string as ok / yellow / red.
 * Accepts "HH:MM" or "H:MM"; returns "ok" for non-time values (no flag).
 * Returns "ok" for empty/null so we don't double-mark empty cells.
 */
export function plausibility(value: string | null | undefined): Plausibility {
  if (!value) return "ok";
  const m = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return "ok";
  const min = Number(m[2]);
  if (!Number.isFinite(min)) return "ok";
  if (QUARTER.has(min)) return "ok";
  if (FIVE_MIN.has(min)) return "yellow";
  return "red";
}

/**
 * Human-readable explanation for a UI tooltip.
 */
export function plausibilityReason(value: string | null | undefined): string {
  const p = plausibility(value);
  if (p === "ok") return "";
  if (!value) return "";
  const min = Number(value.trim().match(/^(\d{1,2}):(\d{2})$/)?.[2] ?? -1);
  if (p === "yellow") {
    return `${value}: Minuten ${String(min).padStart(2, "0")} sind im 5-Min-Raster — ungewöhnlich (typisch sind 00/15/30/45)`;
  }
  return `${value}: Minuten ${String(min).padStart(2, "0")} sind atypisch — wahrscheinlich OCR-Lesefehler (erwartet 00/15/30/45)`;
}


// ─── Sequence Plausibility (day-level chronology) ─────────────────────────
//
// Rule: within one day, times must follow chronological order
//   von1 < bis1 < von2 < bis2.
// Missing values are skipped (e.g. day with only morning shift: von1/bis1
// set, von2/bis2 empty → only von1<bis1 is checked).
// Severity = red whenever any pair is violated — sequence breaks are far
// more serious than minute-rounding atypicality.

export interface DayTimes {
  von1?: string;
  bis1?: string;
  von2?: string;
  bis2?: string;
  symb?: string;
}

/**
 * Parse "HH:MM" → minutes-since-midnight (0..1439). null if unparseable/empty.
 */
function parseTimeMin(s: string | undefined | null): number | null {
  if (!s) return null;
  const m = s.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]), mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  return h * 60 + mm;
}

export type TimeField = "von1" | "bis1" | "von2" | "bis2";

/**
 * Return per-field array of human-readable sequence violations for the day.
 * Empty object = all pairs OK or insufficient data to compare.
 *
 * Example: {von1: "10:00", bis1: "08:00"} →
 *   { von1: ["von1 (10:00) muss vor bis1 (08:00) liegen"],
 *     bis1: ["bis1 (08:00) muss nach von1 (10:00) liegen"] }
 */
export function sequenceViolations(day: DayTimes): Partial<Record<TimeField, string[]>> {
  const v1 = parseTimeMin(day.von1);
  const b1 = parseTimeMin(day.bis1);
  const v2 = parseTimeMin(day.von2);
  const b2 = parseTimeMin(day.bis2);
  const out: Partial<Record<TimeField, string[]>> = {};
  const add = (field: TimeField, msg: string) => {
    (out[field] ??= []).push(msg);
  };

  if (v1 !== null && b1 !== null && v1 >= b1) {
    add("von1", `von1 (${day.von1}) muss VOR bis1 (${day.bis1}) liegen`);
    add("bis1", `bis1 (${day.bis1}) muss NACH von1 (${day.von1}) liegen`);
  }
  if (b1 !== null && v2 !== null && b1 >= v2) {
    add("bis1", `bis1 (${day.bis1}) muss VOR von2 (${day.von2}) liegen — Pause fehlt oder reverse`);
    add("von2", `von2 (${day.von2}) muss NACH bis1 (${day.bis1}) liegen`);
  }
  if (v2 !== null && b2 !== null && v2 >= b2) {
    add("von2", `von2 (${day.von2}) muss VOR bis2 (${day.bis2}) liegen`);
    add("bis2", `bis2 (${day.bis2}) muss NACH von2 (${day.von2}) liegen`);
  }
  return out;
}

/**
 * Aggregate plausibility for one cell: combines value-based (08:03 atypisch)
 * with sequence-based (von1 >= bis1) checks. Sequence violations always escalate
 * to red. Returns merged severity + array of human-readable reasons for tooltip.
 */
export interface CellPlausibility {
  severity: Plausibility;
  reasons: string[];
}

export function cellPlausibility(
  field: TimeField | "symb",
  value: string | null | undefined,
  day: DayTimes,
): CellPlausibility {
  const reasons: string[] = [];
  let severity: Plausibility = "ok";

  // Rule C: Symbol-Exclusivity — when day.symb is set (Krank, Urlaub, Feiertag, …)
  // the day is "off" and no time values are expected. ANY time value alongside
  // a symbol is therefore atypical and likely an OCR misread (either the symbol
  // is wrong or the times bled in from a neighbour row).
  const hasSymb = !!(day.symb && day.symb.trim());
  if (hasSymb && field !== "symb") {
    if (value && value.trim()) {
      reasons.push(`Symbol "${day.symb}" gesetzt — Zeit "${value}" widerspricht (Krank/Urlaub/Feiertag sollte kein Zeitfeld haben)`);
      severity = "red";
    }
  }
  if (hasSymb && field === "symb") {
    // Mirror check on the symb cell itself: if any time is set, the symbol is suspicious.
    const hasAnyTime = (["von1","bis1","von2","bis2"] as const).some(k => day[k] && day[k]!.trim());
    if (hasAnyTime) {
      reasons.push(`Symbol "${value}" gesetzt — aber Zeitwerte vorhanden (von1/bis1/...). Eines davon ist falsch`);
      severity = "red";
    }
  }

  if (field !== "symb") {
    // Value-based (minute-rounding)
    const vp = plausibility(value);
    if (vp !== "ok") {
      const r = plausibilityReason(value);
      if (r) reasons.push(r);
      if (severity !== "red") severity = vp;
    }
    // Rule F (#318): Uhrzeit-Range — gelesene Zeiten vor 07:00 oder nach 20:00 sind
    // oft OCR-Lesefehler (z.B. 4:00 statt 8:00), formal aber gültig und leicht zu
    // übersehen. Gelbe Warnung (wegklickbar), kein harter Fehler.
    const tmin = parseTimeMin(value);
    if (tmin !== null && (tmin < 7 * 60 || tmin > 20 * 60)) {
      reasons.push(`${value}: liegt vor 07:00 bzw. nach 20:00 — bitte prüfen, häufiger OCR-Lesefehler (z.B. 4:00 statt 8:00)`);
      if (severity !== "red") severity = "yellow";
    }
    // Sequence-based — escalates to red regardless of value-based result
    const seqVios = sequenceViolations(day)[field as TimeField] ?? [];
    for (const v of seqVios) reasons.push(v);
    if (seqVios.length > 0) severity = "red";

    // Rule D + E: Block-Vollständigkeit. computeDay (worker/src/calc-timesheet.ts)
    // verlangt PAARWEISE befüllte Blöcke: (von1+bis1) UND/ODER (von2+bis2).
    // Jede asymmetrische Befüllung (eine Hälfte leer) ergibt 0h für den Block
    // ohne Warning. Block-Lücken markiere die fehlende Hälfte als red damit
    // der User die Lücke schließt oder den ganzen Block löscht. Anlassfälle:
    //   D (2026-05-19, Karte b36a167a Tag 17): von1+bis2 ohne bis1/von2
    //   E (2026-05-19, follow-up): allgemeiner — jede half-block-Lücke
    const v1set = !!(day.von1 && day.von1.trim());
    const b1set = !!(day.bis1 && day.bis1.trim());
    const v2set = !!(day.von2 && day.von2.trim());
    const b2set = !!(day.bis2 && day.bis2.trim());

    // Block 1: von1 + bis1 müssen zusammen befüllt sein
    if (v1set !== b1set) {
      if (field === "von1" && !v1set) {
        reasons.push(`von1 fehlt — bis1 (${day.bis1}) gesetzt, aber Block-1-Anfang leer. Block wird still als 0h berechnet`);
        severity = "red";
      }
      if (field === "bis1" && !b1set) {
        reasons.push(`bis1 fehlt — von1 (${day.von1}) gesetzt, aber Block-1-Ende leer. Block wird still als 0h berechnet`);
        severity = "red";
      }
    }
    // Block 2: von2 + bis2 müssen zusammen befüllt sein
    if (v2set !== b2set) {
      if (field === "von2" && !v2set) {
        reasons.push(`von2 fehlt — bis2 (${day.bis2}) gesetzt, aber Block-2-Anfang leer. Block wird still als 0h berechnet`);
        severity = "red";
      }
      if (field === "bis2" && !b2set) {
        reasons.push(`bis2 fehlt — von2 (${day.von2}) gesetzt, aber Block-2-Ende leer. Block wird still als 0h berechnet`);
        severity = "red";
      }
    }
    // Pause-Lücke: von1 + bis2 gesetzt, mittlere Felder (bis1 + von2) leer →
    // Anfang und Ende vorhanden, aber die Pause-Anordnung ist unvollständig.
    // Symptomatischer Sonderfall von D/E zusammen.
    if (v1set && b2set && !b1set && !v2set) {
      const missing: string[] = [];
      if (!b1set) missing.push("bis1");
      if (!v2set) missing.push("von2");
      if ((field === "bis1" && !b1set) || (field === "von2" && !v2set)) {
        reasons.push(
          `Pause unvollständig — von1 (${day.von1}) und bis2 (${day.bis2}) gesetzt, aber ${missing.join(" + ")} leer. `
          + `Tag wird sonst still als 0h berechnet`
        );
        severity = "red";
      }
    }
  }

  return { severity, reasons };
}
